/**
 * ServicePickerModal — centered modal for picking a service
 * from the org's Services Library, used by Step 2 of the
 * quotation wizard.
 *
 * Replaces the old material-library dropdown popover. Pattern B
 * from the SAP Concur industry research: centered modal with
 * autosuggest search + category chips, click-to-pick, link to
 * the library page when empty.
 *
 * Keyboard:
 *   Esc       — close
 *   Enter     — pick the top result (when search yields ≥ 1 row)
 *   ⌘/Ctrl-K  — (handled by the page, not this modal) opens
 *               the topbar search; this modal owns its own
 *               local search.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  SERVICE_CATEGORIES,
  getServiceCategoryConfig,
  type ServiceCategory,
  type ServiceLibraryItem,
} from '@/lib/serviceLibrary';
import { useServiceLibrary } from '@/lib/useServiceLibrary';

export type ServicePickerModalProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  onPick: (service: ServiceLibraryItem) => void;
};

export function ServicePickerModal({
  open,
  onClose,
  orgId,
  onPick,
}: ServicePickerModalProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ServiceCategory | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const { data, loading } = useServiceLibrary(orgId, search, category);

  // Reset on open + auto-focus the search field so the user can
  // start typing immediately.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setCategory(null);
    const id = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  // Body scroll lock + Esc to close + Enter to pick top result.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && data.length > 0) {
        // Only fire Enter-to-pick when the search input is
        // focused — otherwise the user might be clicking a
        // category chip with Enter and expects it to filter,
        // not commit.
        if (document.activeElement === searchRef.current) {
          e.preventDefault();
          onPick(data[0]);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, onPick, data]);

  if (!open) return null;

  return (
    <>
      {/* Centered modal backdrop — uses .slide-overlay class for
          the dim + click-outside-to-close behaviour, then a
          custom centered card on top. */}
      <div
        className="slide-overlay open"
        onClick={onClose}
        aria-hidden
        style={{ zIndex: 100 }}
      />
      <div
        role="dialog"
        aria-label="Pick a service"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(640px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 64px)',
          background: '#fff',
          borderRadius: 14,
          boxShadow:
            '0 1px 3px rgba(15,23,42,0.08), 0 20px 60px rgba(15,23,42,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 101,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <i
            className="ph-bold ph-books"
            style={{ fontSize: 18, color: 'var(--brand)' }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              Pick a service
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              From your studio's Services Library
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 0,
              background: 'transparent',
              padding: 6,
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              fontSize: 16,
            }}
          >
            <i className="ph ph-x" />
          </button>
        </div>

        {/* Search */}
        <div
          style={{
            padding: '12px 18px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: '#FAFBFD',
            }}
          >
            <i
              className="ph ph-magnifying-glass"
              style={{ color: 'var(--text-tertiary)' }}
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Search by name, description, HSN…"
              style={{
                flex: 1,
                border: 0,
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
            {search ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  searchRef.current?.focus();
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  padding: 2,
                }}
                aria-label="Clear search"
              >
                <i className="ph ph-x-circle" />
              </button>
            ) : (
              <kbd
                style={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '1px 5px',
                  background: '#fff',
                  fontFamily: 'inherit',
                }}
              >
                ↵
              </kbd>
            )}
          </div>
        </div>

        {/* Category chip row */}
        <div
          style={{
            padding: '6px 18px 10px',
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <CategoryChip
            label="All"
            icon="ph-grid-four"
            active={category === null}
            onClick={() => setCategory(null)}
          />
          {SERVICE_CATEGORIES.map((c) => (
            <CategoryChip
              key={c.key}
              label={c.label}
              icon={c.icon.replace('ph ', '')}
              active={category === c.key}
              onClick={() =>
                setCategory(category === c.key ? null : c.key)
              }
              activeBg={c.tintBg}
              activeColor={c.color}
            />
          ))}
        </div>

        {/* Results list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '4px 12px 12px',
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              Loading services…
            </div>
          ) : data.length === 0 ? (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 28,
                  color: 'var(--text-tertiary)',
                  marginBottom: 8,
                }}
              >
                <i className="ph ph-wrench" />
              </div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {search || category
                  ? 'No services match'
                  : 'Your services library is empty'}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 14,
                  maxWidth: 360,
                  margin: '0 auto 14px',
                }}
              >
                {search || category
                  ? 'Try a different search term or clear the filter.'
                  : 'Add reusable services (3D rendering, brand identity, site supervision …) so you can drop them into every quote with one tap.'}
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  onClose();
                  navigate('/services');
                }}
              >
                <i className="ph-bold ph-plus" /> Manage library
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.map((s, i) => (
                <ServiceRow
                  key={s.id}
                  service={s}
                  topHint={i === 0 && !!search}
                  onClick={() => onPick(s)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer link — always visible so the user can jump to
            the full library page to add / edit, without first
            getting stuck in the empty state. */}
        {data.length > 0 ? (
          <div
            style={{
              padding: '10px 18px',
              borderTop: '1px solid var(--border)',
              background: '#FAFBFD',
              fontSize: 12,
              color: 'var(--text-secondary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              {data.length} service{data.length === 1 ? '' : 's'} ·{' '}
              <kbd
                style={{
                  fontSize: 10,
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '1px 5px',
                  background: '#fff',
                  fontFamily: 'inherit',
                }}
              >
                ↵
              </kbd>{' '}
              picks the top match
            </span>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/services');
              }}
              style={{
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--brand)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Manage library →
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

// ───── Subcomponents ────────────────────────────────────────

function CategoryChip({
  label,
  icon,
  active,
  onClick,
  activeBg,
  activeColor,
}: {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
  activeBg?: string;
  activeColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 10px',
        borderRadius: 999,
        border: active
          ? `1px solid ${activeColor ?? 'var(--brand)'}`
          : '1px solid var(--border)',
        background: active ? (activeBg ?? 'var(--brand-bg)') : '#fff',
        color: active
          ? (activeColor ?? 'var(--brand)')
          : 'var(--text-secondary)',
        fontSize: 11.5,
        fontWeight: active ? 700 : 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'inherit',
      }}
    >
      <i className={`ph ${icon}`} /> {label}
    </button>
  );
}

function ServiceRow({
  service,
  topHint,
  onClick,
}: {
  service: ServiceLibraryItem;
  topHint: boolean;
  onClick: () => void;
}) {
  const cfg = getServiceCategoryConfig(service.category);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 10,
        border: topHint ? `1px solid ${cfg.color}` : '1px solid var(--border)',
        background: '#fff',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'background 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#FAFBFD';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#fff';
      }}
      title={`Pick "${service.name}"`}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: cfg.tintBg,
          color: cfg.color,
          display: 'grid',
          placeItems: 'center',
          fontSize: 18,
          flexShrink: 0,
        }}
      >
        <i className={cfg.icon} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {service.name}
          </span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: cfg.color,
              background: cfg.tintBg,
              padding: '1px 6px',
              borderRadius: 999,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            {cfg.label}
          </span>
        </div>
        {service.description ? (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }}
          >
            {service.description}
          </div>
        ) : null}
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginTop: 4,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {typeof service.defaultRate === 'number' && service.defaultRate > 0
            ? `₹${service.defaultRate.toLocaleString('en-IN')} / ${service.unit}`
            : `No default rate · ${service.unit}`}
          {service.hsn ? ` · HSN ${service.hsn}` : ''}
        </div>
      </div>
      <i
        className="ph-bold ph-caret-right"
        style={{
          color: 'var(--text-tertiary)',
          fontSize: 14,
          marginTop: 12,
          flexShrink: 0,
        }}
      />
    </button>
  );
}
